<template>
  <!-- 全站顶栏；整幅背景见 branding.header + headerSurfaceStyle / header--image-bg -->
  <div
    class="header"
    :class="[headerThemeClass, headerBrandLayoutClass, { 'header--with-slogan': headerHasSlogan }]"
    :style="headerSurfaceStyle">
    <div class="header-top-line">
      <div class="header-flex">
        <!-- 品牌未配置 slogan：单图顶栏 -->
        <img v-if="!headerHasSlogan" class="header-logo" :src="headerLogo" />
        <!-- 配置了 slogan：logo + 标语（底边对齐） -->
        <div v-else class="header-logo header-logo--with-slogan">
          <img class="header-logo-img" :src="headerLogo" />
          <span class="header-logo-text">{{ headerSlogan }}</span>
        </div>
        <div class="header-nav-shell">
          <div
            class="header-link-wrap"
            ref="navScrollWrap"
            @mousedown="onNavScrollMouseDown"
            @scroll.passive="updateNavScrollEdges">
            <div class="header-link">
              <div v-for="item in firstRowNavList" :key="item.path" class="header-link-item">
                <div
                  class="item-text"
                  :style="{ color: isFirstRowItemActive(item) ? '#f9f600' : '#fff' }"
                  @click="onFirstRowNavClick(item)">
                  {{ item.name }}
                </div>
                <div class="item-line"></div>
              </div>
            </div>
          </div>
          <div v-show="navScrollHint.left" class="header-nav-edge header-nav-edge--left" aria-hidden="true"></div>
          <div v-show="navScrollHint.right" class="header-nav-edge header-nav-edge--right" aria-hidden="true"></div>
        </div>
        <el-dropdown class="header-language" trigger="click" @command="changeLanguage">
          <el-button size="small" style="padding: 9px 8px">
            {{ $language.language === 'en' ? '中文' : 'English' }}
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="zh">中文</el-dropdown-item>
            <el-dropdown-item command="en">English</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <el-dropdown class="header-person head-search-dropdown" trigger="click" @command="handleCommand">
          <el-button size="small" style="padding: 9px 8px">
            {{ $t('common.personalCenter') }}<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <!-- 退出登录 -->
            <el-dropdown-item command="exit">{{ $t('common.logout') }}</el-dropdown-item>
            <!-- 修改密码 -->
            <el-dropdown-item command="password">{{ $t('common.changePassword') }}</el-dropdown-item>
            <!-- 用户留言 -->
            <el-dropdown-item command="userMessage">{{ $t('common.userMessage') }}</el-dropdown-item>
            <!-- 下载剪报工具 -->
            <el-dropdown-item divided @click.native="downloadClipping">{{ $t('common.clippingTool') }}</el-dropdown-item>
            <!-- 下载子光盘工具 -->
            <el-dropdown-item @click.native="downloadRom">{{ $t('common.compactDiscTool') }}</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <i class="header-menu el-icon-s-operation" @click="openMenu"></i>
        <XMenu ref="menu"></XMenu>
        <UserMessageDialog ref="userMessageDialog" />
      </div>
    </div>
    <div v-if="!isCompactHeaderMode && secondNavList.length" class="sub-nav-wrap">
      <div class="sub-nav-inner">
        <div
          v-for="item in secondNavList"
          :key="item.path"
          class="sub-nav-item"
          :class="{ 'sub-nav-item--active': currentPage === item.path }"
          @click="jumpPage(item.path)">
          {{ item.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @file 全站顶栏：Logo、横向菜单（permission 与 PUBLIC_HEADER 回退）、个人中心、侧滑菜单。
 * @description 默认红底 $mainColor；云南日报等可在 branding.header 配置整幅背景图。
 */
import { menuMix } from '@/mixins/menuMix'
import XMenu from '../XMenu/XMenu.vue'
import UserMessageDialog from '../UserMessageDialog/UserMessageDialog.vue'
import { downloadByConnector } from '@/utils/downloadFile.js'
import { debounce } from '@/utils/util.js'
import { getBranding, getThemeKey } from '@/config/branding'
import logoWhite from '@/assets/image/common/logo_white.png'

export default {
  name: 'publicHeader',
  components: { XMenu, UserMessageDialog },
  mixins: [menuMix],
  data() {
    return {
      /** 横向拖动滚动（桌面端） */
      navScrollDrag: {
        active: false,
        moved: false,
        startX: 0,
        startScroll: 0
      },
      /** 左右渐变提示：有未展示内容的一侧才显示 */
      navScrollHint: { left: false, right: false }
    }
  },
  watch: {
    publicList: {
      handler() {
        this.$nextTick(() => this.updateNavScrollEdges())
      },
      deep: true
    },
    compactHeaderNavList: {
      handler() {
        this.$nextTick(() => this.updateNavScrollEdges())
      },
      deep: true
    }
  },
  created() {
    this.syncCurrentFromRoute()
  },
  mounted() {
    this.$nextTick(() => this.updateNavScrollEdges())
    window.addEventListener('resize', this.updateNavScrollEdges)
    this.$root.$on('open-user-message-dialog', this.openUserMessageDialog)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateNavScrollEdges)
    this.teardownNavScrollDrag()
    this.$root.$off('open-user-message-dialog', this.openUserMessageDialog)
  },
  methods: {
    isFirstRowItemActive(item) {
      const current = this.currentPage || ''
      if (this.isCompactHeaderMode) {
        if (item.path === current) return true
        return item.path !== '/' && current.indexOf(item.path) === 0
      }
      return this.activeTopPath === item.path
    },
    onFirstRowNavClick(item) {
      if (this.isCompactHeaderMode) {
        this.onNavItemClick(item.path)
        return
      }
      this.onTopNavClick(item)
    },
    updateNavScrollEdges() {
      const el = this.$refs.navScrollWrap
      if (!el) {
        return
      }
      const max = el.scrollWidth - el.clientWidth
      if (max <= 2) {
        this.navScrollHint = { left: false, right: false }
        return
      }
      const left = el.scrollLeft > 2
      const right = el.scrollLeft < max - 2
      this.navScrollHint = { left, right }
    },
    /** 避免横向拖动松手时误触发菜单跳转 */
    onNavItemClick(path) {
      if (this.navScrollDrag.moved) {
        this.navScrollDrag.moved = false
        return
      }
      if (path == this.$route.path) {
        this.$router.go(0)
        return
      }
      this.jumpPage(path)
    },
    onTopNavClick(item) {
      if (this.navScrollDrag.moved) {
        this.navScrollDrag.moved = false
        return
      }
      const group = (this.headerMenuGroups || []).find((g) => g.path === item.path)
      if (group && group.children && group.children.length) {
        this.jumpPage(group.children[0].path)
        return
      }
      this.jumpPage(item.path)
    },
    onNavScrollMouseDown(e) {
      if (e.button !== 0) {
        return
      }
      const el = this.$refs.navScrollWrap
      if (!el || el.scrollWidth <= el.clientWidth) {
        return
      }
      this.navScrollDrag = {
        active: true,
        moved: false,
        startX: e.pageX,
        startScroll: el.scrollLeft
      }
      document.addEventListener('mousemove', this.onNavScrollMouseMove)
      document.addEventListener('mouseup', this.onNavScrollMouseUp)
    },
    onNavScrollMouseMove(e) {
      if (!this.navScrollDrag.active) {
        return
      }
      const el = this.$refs.navScrollWrap
      if (!el) {
        return
      }
      const dx = e.pageX - this.navScrollDrag.startX
      if (Math.abs(dx) > 4) {
        this.navScrollDrag.moved = true
      }
      el.scrollLeft = this.navScrollDrag.startScroll - dx
      this.updateNavScrollEdges()
    },
    onNavScrollMouseUp() {
      this.teardownNavScrollDrag()
      setTimeout(() => {
        if (this.navScrollDrag.moved) {
          this.navScrollDrag.moved = false
        }
      }, 0)
    },
    teardownNavScrollDrag() {
      this.navScrollDrag.active = false
      document.removeEventListener('mousemove', this.onNavScrollMouseMove)
      document.removeEventListener('mouseup', this.onNavScrollMouseUp)
    },
    changeLanguage(language) {
      this.$language.setLanguage(language)
    },
    openMenu() {
      this.$refs.menu.openMenu()
    },
    openUserMessageDialog() {
      const dlg = this.$refs.userMessageDialog
      dlg && dlg.open()
    },
    downloadClipping: debounce(function () {
      downloadByConnector('/np/gaNpNews/exportCutPaperTool', {}, 'zip', this.$t('common.clippingTool'), 'clipping')
    }, 500),
    downloadRom: debounce(function () {
      downloadByConnector('/np/gaNpNews/exportSubDiscTool', {}, 'zip', this.$t('common.compactDiscTool'), 'compactDisc')
    }, 500)
  },
  computed: {
    headerLogo() {
      return getBranding().logos.header || logoWhite
    },
    headerSlogan() {
      return getBranding().slogan || ''
    },
    /** 任意品牌在 branding 中配置了非空 slogan 时，顶栏使用 logo+标语 布局 */
    headerHasSlogan() {
      return Boolean(String(this.headerSlogan || '').trim())
    },
    /**
     * 顶栏背景：仅当品牌在 branding.header 中配置了 backgroundImage 时返回内联样式。
     * 未配置时返回空对象，沿用 .header-top-line 的 $mainColor。
     */
    headerSurfaceStyle() {
      const h = getBranding().header
      if (!h || !h.backgroundImage) {
        return {}
      }
      return {
        backgroundImage: `url("${h.backgroundImage}")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: h.backgroundPosition || 'center center',
        backgroundSize: h.backgroundSize || 'cover'
      }
    },
    /**
     * 有整幅顶栏背景时附加 class，用于让 .header-top-line 透明、覆盖内边距与 .header-nav-edge 渐变（见 style 中 .header--image-bg）
     */
    headerThemeClass() {
      const h = getBranding().header
      return h && h.backgroundImage ? 'header--image-bg' : ''
    },
    /** whb 顶栏 logo 为横条整图，限制高度避免撑破顶栏 */
    headerBrandLayoutClass() {
      const key = getThemeKey()
      if (key === 'whb') {
        return 'header--whb-brand'
      }
      if (key === 'shzbsh') {
        return 'header--shzbsh-brand'
      }
      return ''
    },
    firstRowNavList() {
      return this.isCompactHeaderMode ? this.compactHeaderNavList : this.topNavList
    }
  }
}
</script>

<style lang="scss" scoped>

.header {
  background: transparent;
}

.header-top-line {
  width: 100%;
  background: $mainColor;
}

/* 整幅顶栏图铺在根节点时，避免中间条再盖一层纯色 */
.header.header--image-bg .header-top-line {
  background-color: transparent;
}

.header {
  .header-flex {
    @include flex-style(center, space-between);
    @include position-top(sticky, 0);
    background: transparent;
    box-sizing: border-box;
    z-index: 2000;
    padding: 6px 0;
    width: 100%;
    @media screen and (min-width: 992px) {
      width: 970px;
    }
    @media screen and (min-width: 1200px) {
      width: 1170px;
    }
    @media screen and (min-width: 1400px) {
      width: 1370px;
    }
    .header-logo {
      vertical-align: middle;
      flex-shrink: 0;
    }
  }
  .header-nav-shell {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: stretch;
  }

  .header-link-wrap {
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    user-select: none;
    cursor: grab;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }

    &:active {
      cursor: grabbing;
    }
  }

  .header-nav-edge {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 28px;
    max-width: 12%;
    pointer-events: none;
    z-index: 2;
  }

  .header-nav-edge--left {
    left: 0;
    background: linear-gradient(90deg, $mainColor 0%, rgba($mainColor, 0.92) 35%, transparent 100%);
  }

  .header-nav-edge--right {
    right: 0;
    background: linear-gradient(270deg, $mainColor 0%, rgba($mainColor, 0.92) 35%, transparent 100%);
  }

  /* 菜单可横向滚动时两侧的提示条：整幅顶栏图时改为暗色半透明，避免与摄影/插画冲突 */
  &.header--image-bg {
    .header-flex {
      padding: 5px 0;
    }
    .header-nav-edge--left {
      background: linear-gradient(90deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0.08) 40%, transparent 100%);
    }
    .header-nav-edge--right {
      background: linear-gradient(270deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0.08) 40%, transparent 100%);
    }
  }

  .header-link {
    @include flex-style(center, flex-start);
    flex-wrap: nowrap;
    width: max-content;
    .header-link-item {
      flex-shrink: 0;
      position: relative;
      .item-text {
        font-weight: 900;
        color: #fff;
        cursor: pointer;
        white-space: nowrap;
      }
      .item-line {
        @include base-style(0px, 2px);
        @include position-bottom(absolute, -7px);
        transition: width 0.3s;
      }
    }
    .header-link-item:first-child {
      margin-left: 0;
    }
    .header-link-item:hover {
      .item-text {
        color: $clickColor !important;
      }

      .item-line {
        width: 100%;
        background: $clickColor;
      }
    }
  }
  .header-menu {
    color: #fff;
    font-size: 30px;
    display: none;
  }
  .header-person {
    flex-shrink: 0;
  }
  @media screen and (min-width: 992px) {
    .header-logo {
      width: 270px;
    }
    .header-link {
      padding: 0 15px;
      .header-link-item {
        margin-left: 15px;
        .item-text {
          font-size: 16px;
        }
      }
    }
  }
  // 大桌面显示器
  @media screen and (min-width: 1200px) {
    .header-logo {
      width: 320px;
    }
    .header-link {
      padding: 0 25px;
      .header-link-item {
        margin-left: 15px;
        .item-text {
          font-size: 18px;
        }
      }
    }
  }
  // 大桌面显示器
  @media screen and (min-width: 1400px) {
    .header-logo {
      width: 400px;
    }
    .header-link {
      padding: 0 40px;
      .header-link-item {
        margin-left: 25px;
        .item-text {
          font-size: 20px;
        }
      }
    }
  }
}

.sub-nav-wrap {
  background: rgba(255, 255, 255, 0);
  border-top: none;
  border-bottom: none;
  margin-top: 3px;
}

.sub-nav-inner {
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1px 0;
  @media screen and (min-width: 992px) {
    width: 970px;
  }
  @media screen and (min-width: 1200px) {
    width: 1170px;
  }
  @media screen and (min-width: 1400px) {
    width: 1370px;
  }
}

.sub-nav-item {
  cursor: pointer;
  padding: 10px 28px;
  color: #333;
  background: rgba(255, 255, 255, 0);
  border-right: 1px solid #d6d6d6;
}

.sub-nav-item--active {
  color: #fff;
  background: $mainColor;
}
@media screen and (max-width: 991px) {
  .header {
    display: block;
    padding: 5px 20px;
    .header-logo {
      width: 270px;
    }
    .header-link {
      display: none;
    }
    .header-person {
      display: none;
    }
    .header-menu {
      @include position-right(absolute, 0, 0);
      display: block;
    }
  }
  .sub-nav-wrap {
    display: none;
  }
}
@media screen and (max-width: 576px) {
  .header {
    padding: 5px 15px;
    .header-logo {
      width: 240px;
    }
  }
}

.header--with-slogan {
  .header-logo--with-slogan {
    width: auto !important;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    flex-wrap: nowrap;
  }
  .header-logo-img {
    display: block;
    flex-shrink: 0;
  }
  .header-logo-text {
    font-weight: 900;
    color: #fff;
    line-height: 1;
    flex-shrink: 0;
  }
  @media screen and (min-width: 992px) {
    .header-logo-img {
      width: 138px;
    }
    .header-logo-text {
      font-size: 18px;
    }
  }
  @media screen and (min-width: 1200px) {
    .header-logo-img {
      width: 180px;
    }
    .header-logo-text {
      font-size: 25px;
    }
  }
  @media screen and (min-width: 1400px) {
    .header-logo-img {
      width: 200px;
    }
    .header-logo-text {
      font-size: 30px;
    }
  }
}

@media screen and (max-width: 991px) {
  .header--with-slogan {
    .header-menu {
      @include position-right(absolute, 0, 13px);
    }
    .header-logo-img {
      width: 140px;
    }
    .header-logo-text {
      font-size: 18px;
    }
  }
}

@media screen and (max-width: 576px) {
  .header--with-slogan {
    .header-logo-img {
      width: 125px;
    }
    .header-logo-text {
      font-size: 18px;
      display: block;
      margin-left: 0;
    }
  }
}

/* 文汇新民：顶栏为 common/logo_header（与首页内 header_brand 长条图区分） */
.header--whb-brand {
  .header-logo {
    width: auto !important;
    max-width: min(300px, 48vw);
    max-height: 44px;
    height: auto;
    object-fit: contain;
    object-position: left center;
  }
  @media screen and (max-width: 991px) {
    .header-logo {
      max-width: min(260px, 70vw);
      max-height: 40px;
    }
  }
  /* 与 header--with-slogan 同挂在根 .header 上，用 & 组合而非后代 */
  &.header--with-slogan .header-logo-img {
    max-height: 44px;
    width: auto;
    max-width: min(300px, 48vw);
    object-fit: contain;
  }
  @media screen and (max-width: 991px) {
    &.header--with-slogan .header-logo-img {
      max-height: 40px;
      max-width: min(260px, 70vw);
    }
  }
}

.header--shzbsh-brand {
  .header-logo {
    width: auto !important;
    max-width: min(300px, 48vw);
    max-height: 44px;
    height: auto;
    object-fit: contain;
    object-position: left center;
  }
}
</style>
